import {
  GroupAnalysisType,
  type WorkLogStatus,
} from '@/generated/types/navigator/types.gen';
import type { TagField } from '@/features/types';
import { NetworkFilters } from './NetworkFilters';
import { RiskAndVegetationFilters } from './RiskAndVegetationFilters';
import { WorkStatusFilter } from './WorkStatusFilter';
import AnalysisYearFilter from './AnalysisYear';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import type { Species } from '@/features/fieldSlice';
import type { Control, UseFormRegister } from 'react-hook-form';
import type { FilterState } from '@/features/filterSlice';
import { useAppSelector } from '@/app/hooks';

import classes from './Filter.module.css';
import {
  type FilterConfig,
  getNetworkFilterVisibility,
} from './filterConstants';

interface FilterFormProps {
  // Form control props
  register: UseFormRegister<FilterState>;
  control: Control<FilterState>;

  // Data props
  sortedSpecies?: Species[];
  tags?: TagField[];
  worklog?: WorkLogStatus[];
  redFlagWarnings?: RedFlagWarning[];

  // Field availability flags
  hasWildfireRisk: boolean;
  hasHazardTrees: boolean;
  hasStrikeTrees: boolean;
  hasCriticalTrees: boolean;
  hasOverhang: boolean;
  hasUpdatedAtYear: boolean;

  // Loading state
  isFetching: boolean;

  // Filter visibility
  filterConfig?: FilterConfig;

  // Whether to show titles for filter group sections
  showTitles?: boolean;
}

export function FilterForm({
  register,
  control,
  sortedSpecies,
  tags,
  worklog,
  redFlagWarnings,
  hasWildfireRisk,
  hasHazardTrees,
  hasStrikeTrees,
  hasCriticalTrees,
  hasOverhang,
  hasUpdatedAtYear,
  isFetching,
  filterConfig,
  showTitles = true,
}: FilterFormProps) {
  const { selectedAnalysis } = useAppSelector((s) => s.analysisMap);

  // We hide some fields in the Network Filter, which is a bit more
  // complex than other filters that are simply shown/hidden.
  const networkVisibility = getNetworkFilterVisibility(filterConfig);
  const shouldShowNetwork = Object.values(networkVisibility).some(Boolean);

  const shouldShowRiskAndVegetation = filterConfig?.riskAndVegetation !== false;
  const shouldShowWorkStatus = filterConfig?.workStatus !== false;
  const shouldShowAnalysisYear = filterConfig?.analysisYear !== false;

  const formInputs = (
    <>
      {shouldShowNetwork && (
        <NetworkFilters
          register={register}
          control={control}
          showTitle={showTitles}
          showNetworkSelector={networkVisibility.networkSelector}
          showGroups={networkVisibility.groups}
          showPhase={networkVisibility.phase}
          showVoltage={networkVisibility.voltage}
        />
      )}
      {shouldShowRiskAndVegetation && (
        <RiskAndVegetationFilters
          hasFallInOptionSelected={
            selectedAnalysis === GroupAnalysisType.FALL_IN
          }
          control={control}
          speciesData={sortedSpecies}
          hasHazardTrees={hasHazardTrees}
          hasStrikeTrees={hasStrikeTrees}
          hasCriticalTrees={hasCriticalTrees}
          hasWildfireRisk={hasWildfireRisk}
          hasOverhang={hasOverhang}
          tags={tags}
          redFlagWarnings={redFlagWarnings}
          showTitle={showTitles}
        />
      )}
      {shouldShowWorkStatus && (
        <WorkStatusFilter
          control={control}
          data={worklog ?? []}
          showTitle={showTitles}
        />
      )}
      {shouldShowAnalysisYear && hasUpdatedAtYear && (
        <AnalysisYearFilter control={control} showTitle={showTitles} />
      )}
    </>
  );

  return (
    <>
      {!isFetching && <form className={classes.form}>{formInputs}</form>}
      {isFetching && <div className={classes.loading} />}
    </>
  );
}
